import { useReducer, useRef, useMemo } from "react";
import type {
  PlacePrediction,
  AddressComponents,
} from "./address-search-types";

/**
 * State for the address search field
 * Consolidates multiple useState hooks into a single reducer for better state management
 */
interface AddressSearchState {
  searchQuery: string;
  predictions: PlacePrediction[];
  showSuggestions: boolean;
  isLoading: boolean;
  hasSearched: boolean;
  hasError: boolean;
  errorMessage: string | null;
  selectedAddress: AddressComponents | null;
  manualEntry: boolean;
  activeIndex: number;
  hasTyped: boolean;
}

/**
 * Actions for the address search reducer
 */
type AddressSearchAction =
  | { type: "SET_SEARCH_QUERY"; payload: string }
  | { type: "SET_PREDICTIONS"; payload: PlacePrediction[] }
  | { type: "SET_SHOW_SUGGESTIONS"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_HAS_SEARCHED"; payload: boolean }
  | {
      type: "SET_ERROR";
      payload: { hasError: boolean; message: string | null };
    }
  | { type: "SET_SELECTED_ADDRESS"; payload: AddressComponents | null }
  | { type: "SET_MANUAL_ENTRY"; payload: boolean }
  | { type: "SET_ACTIVE_INDEX"; payload: number }
  | { type: "SET_HAS_TYPED"; payload: boolean }
  | { type: "RESET_SEARCH" }
  | { type: "DISMISS_ADDRESS" }
  | { type: "SEARCH_SUCCESS"; payload: PlacePrediction[] }
  | { type: "SEARCH_ERROR"; payload: string }
  | { type: "SEARCH_NO_RESULTS" };

/**
 * Initial state for the address search field
 */
const initialState: AddressSearchState = {
  searchQuery: "",
  predictions: [],
  showSuggestions: false,
  isLoading: false,
  hasSearched: false,
  hasError: false,
  errorMessage: null,
  selectedAddress: null,
  manualEntry: false,
  activeIndex: -1,
  hasTyped: false,
};

/**
 * Reducer for address search state
 * Consolidates state updates and ensures consistency
 */
function addressSearchReducer(
  state: AddressSearchState,
  action: AddressSearchAction,
): AddressSearchState {
  switch (action.type) {
    case "SET_SEARCH_QUERY":
      return {
        ...state,
        searchQuery: action.payload,
        activeIndex: -1, // Reset active index when query changes
      };

    case "SET_PREDICTIONS":
      return { ...state, predictions: action.payload };

    case "SET_SHOW_SUGGESTIONS":
      return { ...state, showSuggestions: action.payload };

    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_HAS_SEARCHED":
      return { ...state, hasSearched: action.payload };

    case "SET_ERROR":
      return {
        ...state,
        hasError: action.payload.hasError,
        errorMessage: action.payload.message,
      };

    case "SET_SELECTED_ADDRESS":
      return { ...state, selectedAddress: action.payload };

    case "SET_MANUAL_ENTRY":
      return { ...state, manualEntry: action.payload };

    case "SET_ACTIVE_INDEX":
      return { ...state, activeIndex: action.payload };

    case "SET_HAS_TYPED":
      return { ...state, hasTyped: action.payload };

    case "RESET_SEARCH":
      return {
        ...state,
        searchQuery: "",
        predictions: [],
        showSuggestions: false,
        hasSearched: false,
        hasError: false,
        errorMessage: null,
        hasTyped: false,
        activeIndex: -1,
      };

    case "DISMISS_ADDRESS":
      return {
        ...state,
        selectedAddress: null,
        searchQuery: "",
        predictions: [],
        showSuggestions: false,
        hasSearched: false,
        hasError: false,
        errorMessage: null,
        hasTyped: false,
        activeIndex: -1,
      };

    case "SEARCH_SUCCESS":
      return {
        ...state,
        predictions: action.payload,
        showSuggestions: true,
        hasSearched: true,
        hasError: false,
        errorMessage: null,
        isLoading: false,
      };

    case "SEARCH_ERROR":
      return {
        ...state,
        predictions: [],
        showSuggestions: true,
        hasSearched: false,
        hasError: true,
        errorMessage: action.payload,
        isLoading: false,
      };

    case "SEARCH_NO_RESULTS":
      return {
        ...state,
        predictions: [],
        showSuggestions: true,
        hasSearched: true,
        hasError: false,
        errorMessage: null,
        isLoading: false,
      };

    default:
      return state;
  }
}

/**
 * Hook for managing address search state
 * Consolidates multiple useState hooks and provides action creators
 */
export function useAddressSearchState() {
  const [state, dispatch] = useReducer(addressSearchReducer, initialState);

  // Refs for cleanup and request management
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryCacheRef = useRef<Map<string, PlacePrediction[]>>(new Map());

  // Action creators wrapped in useMemo for stable object reference
  const actions = useMemo(
    () => ({
      setSearchQuery: (query: string) => {
        dispatch({ type: "SET_SEARCH_QUERY", payload: query });
      },

      setPredictions: (predictions: PlacePrediction[]) => {
        dispatch({ type: "SET_PREDICTIONS", payload: predictions });
      },

      setShowSuggestions: (show: boolean) => {
        dispatch({ type: "SET_SHOW_SUGGESTIONS", payload: show });
      },

      setLoading: (loading: boolean) => {
        dispatch({ type: "SET_LOADING", payload: loading });
      },

      setHasSearched: (searched: boolean) => {
        dispatch({ type: "SET_HAS_SEARCHED", payload: searched });
      },

      setError: (hasError: boolean, message: string | null = null) => {
        dispatch({ type: "SET_ERROR", payload: { hasError, message } });
      },

      setSelectedAddress: (address: AddressComponents | null) => {
        dispatch({ type: "SET_SELECTED_ADDRESS", payload: address });
      },

      setManualEntry: (manual: boolean) => {
        dispatch({ type: "SET_MANUAL_ENTRY", payload: manual });
      },

      setActiveIndex: (index: number) => {
        dispatch({ type: "SET_ACTIVE_INDEX", payload: index });
      },

      setHasTyped: (typed: boolean) => {
        dispatch({ type: "SET_HAS_TYPED", payload: typed });
      },

      resetSearch: () => {
        dispatch({ type: "RESET_SEARCH" });
      },

      dismissAddress: () => {
        dispatch({ type: "DISMISS_ADDRESS" });
      },

      searchSuccess: (predictions: PlacePrediction[]) => {
        dispatch({ type: "SEARCH_SUCCESS", payload: predictions });
      },

      searchError: (message: string) => {
        dispatch({ type: "SEARCH_ERROR", payload: message });
      },

      searchNoResults: () => {
        dispatch({ type: "SEARCH_NO_RESULTS" });
      },
    }),
    [], // Empty deps - dispatch is stable
  );

  return {
    state,
    actions,
    refs: {
      debounceTimer: debounceTimerRef,
      abortController: abortControllerRef,
      queryCache: queryCacheRef,
    },
  };
}

/**
 * Derived state and computed values
 * Replaces duplicate state with computed values
 */
export function useAddressSearchDerivedState(
  selectedAddress: AddressComponents | null,
  manualEntry: boolean,
  hasTyped: boolean,
  searchDebounceMs: number,
) {
  // Mode is derived from selected address and manual entry flag
  const mode: "search" | "manual" | "selected" = selectedAddress
    ? "selected"
    : manualEntry
      ? "manual"
      : "search";

  // Adaptive debounce is derived from hasTyped state
  const adaptiveDebounceMs = hasTyped ? 250 : searchDebounceMs;

  return {
    mode,
    adaptiveDebounceMs,
  };
}

/**
 * Export types for use in components
 */
export type { AddressSearchState, AddressSearchAction };
